import React from "react";
import { Link } from "react-router-dom";

/**
 * BrandButton — botón con identidad de marca.
 *
 * En light mode: base oscura/grafito, subrayado rojo deslizante en hover.
 * En dark mode: usa los tokens semánticos de DaisyUI (btn-primary, etc.).
 *
 * Props:
 *   as        — "button" | "link"  (default: "button")
 *   to        — ruta para cuando as="link"
 *   variant   — "solid" | "outline" | "ghost"  (default: "solid")
 *   size      — "xs" | "sm" | "md" | "lg"      (default: "md")
 *   className — clases extra
 *   children
 *   ...rest   — onClick, disabled, type, etc.
 */

const SIZE = {
    xs:  "px-3 py-1 text-xs",
    sm:  "px-4 py-1.5 text-sm",
    md:  "px-5 py-2 text-sm font-semibold",
    lg:  "px-7 py-3 text-base font-semibold",
};

const BASE =
    "relative inline-flex items-center justify-center gap-2 rounded-lg " +
    "transition-all duration-200 select-none overflow-hidden group " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2 " +
    "disabled:opacity-40 disabled:pointer-events-none";

const VARIANT = {
    solid:
        "bg-brand-dark text-white " +
        "dark:bg-primary dark:text-primary-content " +
        "hover:brightness-90 active:scale-[.97]",
    outline:
        "border border-brand-dark text-brand-dark bg-transparent " +
        "dark:border-primary dark:text-primary " +
        "hover:bg-brand-dark/5 active:scale-[.97]",
    ghost:
        "text-brand-dark bg-transparent " +
        "dark:text-primary " +
        "hover:bg-brand-dark/5 active:scale-[.97]",
};

// Subrayado rojo deslizante — sólo visible en light (dark ya tiene su estética)
function RedUnderline() {
    return (
        <span
            aria-hidden
            className="
                absolute bottom-0 left-0 h-[2px] w-0 bg-brand-red
                transition-[width] duration-300 ease-out
                group-hover:w-full
                dark:hidden
            "
        />
    );
}

export default function BrandButton({
    as = "button",
    to,
    variant = "solid",
    size = "md",
    className = "",
    children,
    ...rest
}) {
    const cls = `${BASE} ${SIZE[size] ?? SIZE.md} ${VARIANT[variant] ?? VARIANT.solid} ${className}`;

    if (as === "link" && to) {
        return (
            <Link to={to} className={cls} {...rest}>
                {children}
                <RedUnderline />
            </Link>
        );
    }

    return (
        <button className={cls} {...rest}>
            {children}
            <RedUnderline />
        </button>
    );
}
